import { useRef, useEffect, useMemo } from 'react';
import ApexCharts from 'apexcharts';
import type { ApexOptions } from 'apexcharts';
import type { ChartConfig, ChartType } from '@mem-ai-hub/shared';

const BRAND_COLORS = ['#03A9F4', '#E05726', '#4CAF50', '#FF9800', '#9C27B0', '#009688'];
const VALID_CHART_TYPES: ChartType[] = ['bar', 'line', 'area', 'pie', 'donut', 'radar'];

export function tryParseChart(text: string): ChartConfig | null {
  try {
    const obj = JSON.parse(text);
    if (!obj || typeof obj !== 'object') return null;

    // Resolve chartType from either format:
    //   1. { type: 'chart', chartType: 'bar', ... }   (canonical)
    //   2. { chartType: 'bar', ... }                   (type omitted)
    //   3. { type: 'bar', ... }                        (type IS the chart type)
    let chartType: string | undefined;
    if (typeof obj.chartType === 'string' && VALID_CHART_TYPES.includes(obj.chartType as ChartType)) {
      chartType = obj.chartType;
    } else if (typeof obj.type === 'string' && VALID_CHART_TYPES.includes(obj.type as ChartType)) {
      chartType = obj.type;
    }
    if (!chartType) return null;

    // Validate series data exists
    if (!obj.data || !Array.isArray(obj.data.series) || obj.data.series.length === 0) return null;

    // Normalize to canonical ChartConfig shape
    return {
      type: 'chart',
      chartType: chartType as ChartType,
      title: obj.title,
      data: obj.data,
      colors: obj.colors,
      stacked: obj.stacked,
      horizontal: obj.horizontal,
    };
  } catch {
    // not valid chart JSON
  }
  return null;
}

interface ChartBlockProps {
  config: ChartConfig;
}

function buildOptions(config: ChartConfig, isDark: boolean): { options: ApexOptions; series: ApexOptions['series'] } {
  const { chartType, title, data, colors = BRAND_COLORS, stacked, horizontal } = config;
  const isPie = chartType === 'pie' || chartType === 'donut';
  const foreColor = isDark ? '#94A3B8' : '#4a5568';

  const baseOptions: ApexOptions = {
    chart: {
      type: chartType,
      height: 350,
      width: '100%',
      animations: { enabled: true, speed: 1, animateGradually: { enabled: false } },
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: !isPie,
          zoomin: !isPie,
          zoomout: !isPie,
          pan: !isPie,
          reset: !isPie,
        },
        export: {
          svg: { filename: title ?? 'chart' },
          png: { filename: title ?? 'chart' },
        },
      },
      background: 'transparent',
      stacked: stacked ?? false,
      fontFamily: '"Open Sans", sans-serif',
      foreColor,
    },
    colors,
    title: title
      ? {
          text: title,
          style: {
            fontFamily: '"Raleway", sans-serif',
            fontWeight: '600',
            fontSize: '14px',
            color: foreColor,
          },
        }
      : undefined,
    grid: {
      borderColor: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(128,128,128,0.15)',
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      width: chartType === 'area' ? 2 : chartType === 'line' ? 3 : undefined,
    },
    dataLabels: {
      enabled: isPie,
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
    },
  };

  if (isPie) {
    const flatSeries = data.series[0]?.data ?? [];
    const labels = data.categories ?? data.series[0]?.data.map((_, i) => `Item ${i + 1}`) ?? [];

    return {
      options: {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: chartType },
        labels,
        series: flatSeries,
        legend: {
          position: 'bottom' as const,
          fontFamily: '"Open Sans", sans-serif',
          labels: { colors: foreColor },
        },
      },
      series: flatSeries,
    };
  }

  // Cartesian charts: bar, line, area, radar
  const opts: ApexOptions = {
    ...baseOptions,
    chart: { ...baseOptions.chart, type: chartType },
    series: data.series,
    xaxis: {
      categories: data.categories ?? [],
      labels: { style: { fontSize: '12px', colors: foreColor } },
    },
    yaxis: {
      labels: { style: { fontSize: '12px', colors: [foreColor] } },
    },
    legend: {
      position: 'top' as const,
      fontFamily: '"Open Sans", sans-serif',
      labels: { colors: foreColor },
    },
  };

  if (chartType === 'bar' && horizontal) {
    opts.plotOptions = { bar: { horizontal: true, columnWidth: '55%' } };
  } else if (chartType === 'bar') {
    opts.plotOptions = { bar: { columnWidth: '55%' } };
  }

  if (chartType === 'radar') {
    opts.xaxis = { categories: data.categories ?? [] };
    delete opts.yaxis;
  }

  return { options: opts, series: data.series };
}

export default function ChartBlock({ config }: ChartBlockProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ApexCharts | null>(null);
  const renderingRef = useRef(false);

  const isDark = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

  // Stabilize config: JSON.stringify comparison prevents re-renders during SSE
  // streaming from triggering chart re-initialization
  const configJson = JSON.stringify(config);

  const { options } = useMemo(
    () => buildOptions(config, isDark),
    [configJson, isDark], // configJson stabilizes config identity across streaming re-renders
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // If a render is already in progress, skip — don't destroy mid-render
    if (renderingRef.current) return;

    // Destroy previous chart if it exists
    if (chartRef.current) {
      try { chartRef.current.destroy(); } catch { /* ignore */ }
      chartRef.current = null;
    }

    const chart = new ApexCharts(el, options);
    chartRef.current = chart;
    renderingRef.current = true;

    chart.render().then(() => {
      renderingRef.current = false;
      // Force a non-animated redraw so bars reach their final dimensions.
      // ApexCharts 5.x creates bar rects at animation-start (zero height)
      // and relies on rAF to animate them. During SSE streaming the component
      // remounts and the animation frame is lost. updateSeries with
      // animate=false forces immediate recalculation of bar dimensions.
      if (chartRef.current === chart) {
        setTimeout(() => {
          if (chartRef.current === chart) {
            try {
              const s = options.series;
              if (Array.isArray(s)) {
                chart.updateSeries([...s] as Parameters<typeof chart.updateSeries>[0], false);
              }
            } catch { /* ignore */ }
          }
        }, 50);
      }
    }).catch(() => {
      renderingRef.current = false;
    });

    return () => {
      renderingRef.current = false;
      if (chartRef.current) {
        try { chartRef.current.destroy(); } catch { /* ignore */ }
        chartRef.current = null;
      }
    };
  }, [options]);

  return (
    <div className="my-3 rounded-md border border-border-light bg-bg p-4">
      <div ref={containerRef} />
    </div>
  );
}
